import { Button } from '../components/ui/button';
import { ArrowLeft, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

export const LogoOptionsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="logo-options-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-header shadow-sm">
        <div className="container-width h-20 flex items-center justify-between">
          <span className="text-2xl font-bold text-foreground font-['Manrope']">
            Logo Options
          </span>
          <Link to="/">
            <Button variant="outline" className="rounded-full" data-testid="back-home-btn">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Logo Options */}
      <section className="section-padding">
        <div className="container-width">
          <h1 className="text-3xl font-bold text-center mb-12">Choose Your Logo Style</h1>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Option 1: Current Style */}
            <div className="border border-border rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="text-center mb-6">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Option 1</span>
                <p className="text-sm text-muted-foreground mt-1">Current Style</p>
              </div>
              <div className="flex justify-center items-center h-24 bg-secondary rounded-xl mb-4">
                <span className="text-3xl font-bold text-foreground font-['Manrope']">
                  Digitive<span className="text-primary">Health</span>
                </span>
              </div>
              <div className="flex justify-center items-center h-24 bg-slate-800 rounded-xl">
                <span className="text-3xl font-bold text-white font-['Manrope']">
                  Digitive<span className="text-primary">Health</span>
                </span>
              </div>
            </div>

            {/* Option 2: Gradient Style */}
            <div className="border border-border rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="text-center mb-6">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Option 2</span>
                <p className="text-sm text-muted-foreground mt-1">Gradient Accent</p>
              </div>
              <div className="flex justify-center items-center h-24 bg-secondary rounded-xl mb-4">
                <span className="text-3xl font-bold font-['Manrope']">
                  <span className="text-foreground">Digitive</span>
                  <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Health</span>
                </span>
              </div>
              <div className="flex justify-center items-center h-24 bg-slate-800 rounded-xl">
                <span className="text-3xl font-bold font-['Manrope']">
                  <span className="text-white">Digitive</span>
                  <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Health</span>
                </span>
              </div>
            </div>

            {/* Option 3: Icon + Text */}
            <div className="border border-border rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="text-center mb-6">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Option 3</span>
                <p className="text-sm text-muted-foreground mt-1">Icon + Text</p>
              </div>
              <div className="flex justify-center items-center h-24 bg-secondary rounded-xl mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                    <span className="text-white font-bold text-xl">D</span>
                  </div>
                  <span className="text-2xl font-bold text-foreground font-['Manrope']">
                    DigitiveHealth
                  </span>
                </div>
              </div>
              <div className="flex justify-center items-center h-24 bg-slate-800 rounded-xl">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                    <span className="text-white font-bold text-xl">D</span>
                  </div>
                  <span className="text-2xl font-bold text-white font-['Manrope']">
                    DigitiveHealth
                  </span>
                </div>
              </div>
            </div>

            {/* Option 4: Stacked */}
            <div className="border border-border rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="text-center mb-6">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Option 4</span>
                <p className="text-sm text-muted-foreground mt-1">Stacked Layout</p>
              </div>
              <div className="flex justify-center items-center h-24 bg-secondary rounded-xl mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground font-['Manrope'] tracking-wider">DIGITIVE</div>
                  <div className="text-lg font-medium text-primary tracking-[0.3em]">HEALTH</div>
                </div>
              </div>
              <div className="flex justify-center items-center h-24 bg-slate-800 rounded-xl">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white font-['Manrope'] tracking-wider">DIGITIVE</div>
                  <div className="text-lg font-medium text-primary tracking-[0.3em]">HEALTH</div>
                </div>
              </div>
            </div>

            {/* Option 5: Minimal with Line */}
            <div className="border border-border rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="text-center mb-6">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Option 5</span>
                <p className="text-sm text-muted-foreground mt-1">Minimal with Divider</p>
              </div>
              <div className="flex justify-center items-center h-24 bg-secondary rounded-xl mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-light text-foreground font-['Manrope']">Digitive</span>
                  <div className="w-px h-8 bg-primary"></div>
                  <span className="text-2xl font-bold text-primary font-['Manrope']">Health</span>
                </div>
              </div>
              <div className="flex justify-center items-center h-24 bg-slate-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-light text-white font-['Manrope']">Digitive</span>
                  <div className="w-px h-8 bg-primary"></div>
                  <span className="text-2xl font-bold text-primary font-['Manrope']">Health</span>
                </div>
              </div>
            </div>

            {/* Option 6: Badge Style */}
            <div className="border border-border rounded-2xl p-8 hover:shadow-lg transition-shadow">
              <div className="text-center mb-6">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Option 6</span>
                <p className="text-sm text-muted-foreground mt-1">Badge Style</p>
              </div>
              <div className="flex justify-center items-center h-24 bg-secondary rounded-xl mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                    <span className="text-white font-bold text-lg">DH</span>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-foreground font-['Manrope'] leading-tight">Digitive</div>
                    <div className="text-sm font-medium text-primary leading-tight">Healthcare</div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center items-center h-24 bg-slate-800 rounded-xl">
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                    <span className="text-white font-bold text-lg">DH</span>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-white font-['Manrope'] leading-tight">Digitive</div>
                    <div className="text-sm font-medium text-primary leading-tight">Healthcare</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default LogoOptionsPage;
